import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  business_name: "",
  property_type: "",
  location: "",
  biggest_challenge: "",
};

const PROPERTY_TYPES = [
  "Motel",
  "Hotel",
  "Hostel",
  "Airbnb / Short-stay",
  "Investment property",
  "Holiday home / portfolio",
  "Boutique accommodation",
  "Serviced apartments",
  "Other",
];

export default function AuditForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please add your name, email and phone so we can get back to you.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/audit`, form);
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Audit request received — we'll be in touch within 1 business day.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email aaron@blackrockhospitality.co.nz.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="audit"
      data-testid="audit-form-section"
      className="relative py-24 md:py-36 bg-[#050505] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#C2A878]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#C2A878] font-semibold">
              Mini audit
            </span>
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white tracking-tight">
            Find the revenue you&apos;re <em className="text-[#C2A878] not-italic">leaving on the table.</em>
          </h2>
          <p className="mt-8 text-white/65 leading-relaxed text-base md:text-lg max-w-lg">
            Tell us about your property. Within one business day we&apos;ll come
            back with specific opportunities we see in your operation.
          </p>

          <ul className="mt-10 space-y-4 border-t border-white/10 pt-8 max-w-md">
            {[
              "30-minute call with a real operator",
              "Specific revenue leaks identified",
              "Plain-English next-step recommendation",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-white/75">
                <span className="block w-1.5 h-1.5 rounded-full bg-[#C2A878] mt-2" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="audit-form-success"
              className="bg-[#0B1D28] border border-[#C2A878]/40 p-10 md:p-14"
            >
              <div className="text-xs tracking-[0.3em] uppercase text-[#C2A878]">
                Received
              </div>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl text-white">
                Thank you. We&apos;ll be in touch shortly.
              </h3>
              <p className="mt-6 text-white/70 leading-relaxed">
                A confirmation has been logged. We&apos;ll review your details
                and respond within one business day from{" "}
                <span className="text-[#C2A878]">aaron@blackrockhospitality.co.nz</span>.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="audit-form-reset"
                className="mt-10 inline-flex items-center gap-3 border border-white/20 hover:border-[#C2A878] hover:text-[#C2A878] px-6 py-3 text-xs font-semibold tracking-[0.2em] uppercase transition-all"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="audit-form"
              className="bg-white/[0.025] border border-white/10 p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Full name *"
                  testid="audit-input-full-name"
                  value={form.full_name}
                  onChange={(v) => update("full_name", v)}
                  required
                />
                <Field
                  label="Email *"
                  type="email"
                  testid="audit-input-email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  required
                />
                <Field
                  label="Phone *"
                  type="tel"
                  testid="audit-input-phone"
                  value={form.phone}
                  onChange={(v) => update("phone", v)}
                  required
                />
                <Field
                  label="Business / property name"
                  testid="audit-input-business"
                  value={form.business_name}
                  onChange={(v) => update("business_name", v)}
                />

                <SelectField
                  label="Property type"
                  testid="audit-select-property-type"
                  value={form.property_type}
                  onChange={(v) => update("property_type", v)}
                  options={PROPERTY_TYPES}
                />
                <Field
                  label="Location"
                  testid="audit-input-location"
                  placeholder="e.g. Mount Maunganui"
                  value={form.location}
                  onChange={(v) => update("location", v)}
                />
              </div>

              <div>
                <Label>Any further comments</Label>
                <textarea
                  data-testid="audit-input-challenge"
                  value={form.biggest_challenge}
                  onChange={(e) => update("biggest_challenge", e.target.value)}
                  rows={4}
                  placeholder="Tell us briefly what's not working…"
                  className="mt-2 w-full bg-[#0A0D10] border border-white/15 text-white rounded-none p-4 text-sm placeholder:text-white/30 focus:border-[#C2A878] focus:ring-1 focus:ring-[#C2A878] focus:outline-none transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="audit-form-submit"
                className="group inline-flex items-center gap-3 bg-[#C2A878] hover:bg-[#D4AF37] disabled:bg-[#C2A878]/60 text-[#050505] px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase transition-all duration-300"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Request my mini audit
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <p className="text-xs text-white/40 leading-relaxed">
                We&apos;ll only use your details to follow up on this audit
                request. No newsletters, no spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[10px] tracking-[0.25em] uppercase text-white/55 font-semibold">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0A0D10] border border-white/15 text-white rounded-none px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#C2A878] focus:ring-1 focus:ring-[#C2A878] focus:outline-none transition-colors"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className="mt-2 w-full bg-[#0A0D10] border border-white/15 text-white rounded-none px-4 py-3 h-auto text-sm focus:border-[#C2A878] focus:ring-1 focus:ring-[#C2A878]"
        >
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent className="bg-[#0A0D10] border-white/15 text-white rounded-none">
          {options.map((o) => (
            <SelectItem
              key={o}
              value={o}
              data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="focus:bg-[#C2A878]/15 focus:text-[#C2A878] rounded-none"
            >
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
